import { useCallback, useMemo, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { BackendReportConfig, GetReportsState } from "./types";

/**
 * Hook to fetch report configurations from the backend
 * 
 * Fetches from: GET /wp-json/smassets/v1/reports/
 * 
 * Usage:
 *   const { getReports } = useGetReports();
 *   const { reports, loading, error } = await getReports();
 */
export const useGetReports = () => {
  const [state, setState] = useState<GetReportsState>({
    reports: [],
    loading: false,
    error: null,
  });

  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const WP_ENDPOINT = "wp-json/smassets/v1/reports/";
  const { getApiUrl } = useMultiSiteContext();
  const reportsUrl = useMemo(() => getApiUrl(WP_ENDPOINT), [getApiUrl]);

  const getReports = useCallback(async (): Promise<BackendReportConfig[] | null> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetchWithTokenRefresh(
        reportsUrl,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.authToken ?? ""}`,
          },
        },
        () => refreshUserToken(user?.refreshToken ?? "")
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch reports: ${response.status} ${response.statusText}`
        );
      }

      const json: any = await response.json();

      // Handle the response with flexible parsing
      let rawReports: any[] = [];
      
      // Production format: { reports: [...] }
      if (Array.isArray(json.reports)) {
        rawReports = json.reports;
      } 
      // Debug format with success flag: { success: true, data: { reports: [...] } }
      else if (json.success && json.data?.reports) {
        rawReports = json.data.reports;
      } 
      // Debug format with direct array: { get_org_configs_raw: [...] }
      else if (Array.isArray(json.get_org_configs_raw)) {
        rawReports = json.get_org_configs_raw;
      } 
      // Error cases
      else if (json.success === false) {
        throw new Error(json.message || "Backend returned success: false");
      } else {
        throw new Error("Unexpected API response structure");
      }

      // Flatten the structure: move admin_config properties to root level (if present)
      const reports: BackendReportConfig[] = rawReports
        .filter((config: any) => {
          // Only filter by is_active if the field exists (debug format)
          // If is_active doesn't exist, assume it's active (production format)
          if (config.is_active !== undefined) {
            return config.is_active === "1" || config.is_active === 1 || config.is_active === true;
          }
          return true;
        })
        .map((config: any) => {
          // Handle both debug format (with admin_config) and production format (already flat)
          const adminConfig = config.admin_config || {};
          
          // report_filters may arrive as a JSON string from MySQL
          const reportFilters = Array.isArray(config.report_filters)
            ? config.report_filters
            : typeof config.report_filters === "string" && config.report_filters
            ? JSON.parse(config.report_filters)
            : undefined;

          // permissions may arrive as a JSON string from MySQL
          const permissions = typeof config.permissions === "string" && config.permissions
            ? JSON.parse(config.permissions)
            : config.permissions;

          return {
            id: config.report_id || config.id,
            title: config.title,
            description: config.description,
            report_filters: reportFilters,
            permissions,
            sort_order: typeof config.sort_order === "number" ? config.sort_order : (config.sort_order !== undefined ? Number(config.sort_order) : undefined),
            icon: config.icon,
            config_version: config.config_version || 1,
            type: config.type,
            // Spread admin_config if it exists, otherwise use root-level properties
            ...(Object.keys(adminConfig).length > 0
              ? adminConfig
              : {
                  dataSources: config.dataSources,
                  charts: config.charts,
                  statCards: config.statCards,
                }),
          };
        });

      setState({
        reports,
        loading: false,
        error: null,
      });

      return reports;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error fetching reports";

      setState({
        reports: [],
        loading: false,
        error: errorMessage,
      });

      setError(errorMessage);
      return null;
    }
  }, [reportsUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken]);

  return {
    ...state,
    getReports,
  };
};
